import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, CheckCircle, FolderCheck, Image, User, Clapperboard } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import StatusBadge from '../components/StatusBadge';
import { AnimatedModal } from '../components/AnimatedComponents';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import toast from 'react-hot-toast';

// Frasi motivazionali/tecniche per spronare il videomaker a riprese virali
const FRASI_VIDEOMAKER = [
  'Ricordati di essere creativo con scene dinamiche!',
  'Hai regolato le ISO per la luce di oggi?',
  'Controllato l\'obiettivo della camera? Niente ditate sulla lente!',
  'Un movimento di camera fluido vale più di mille tagli.',
  'Prova un\'inquadratura dal basso: dà potenza al soggetto.',
  'Hai impostato il bilanciamento del bianco?',
  'Metti a fuoco sugli occhi: è lì che nasce la connessione.',
  'Sfrutta la luce naturale, è la tua migliore alleata.',
  'Cambia angolazione a ogni battuta: tieni viva l\'attenzione.',
  'Hai controllato l\'audio? Un reel virale parte da un buon suono.',
  'Stabilizza la ripresa: niente tremolii da principiante.',
  'Gioca con la profondità di campo per uno sfondo cinematografico.',
  'Un B-roll dinamico rende ogni reel più professionale.',
  'Ricordati la regola dei terzi per inquadrature che colpiscono.',
  'Aggiungi un movimento in avvicinamento sull\'hook: cattura subito.',
  'Hai pulito la lente prima di girare?',
  'Sperimenta un rallenty sul momento clou.',
  'Luce negli occhi = sguardo magnetico: controlla il catchlight.',
  'Registra qualche secondo in più: ti serviranno in montaggio.',
  'Varia il ritmo delle riprese per un montaggio più energico.',
  'Osa un\'inquadratura insolita: il virale nasce dal coraggio.',
];

const fraseCasuale = (escludi?: string): string => {
  if (FRASI_VIDEOMAKER.length <= 1) return FRASI_VIDEOMAKER[0];
  let f = escludi;
  while (f === escludi) f = FRASI_VIDEOMAKER[Math.floor(Math.random() * FRASI_VIDEOMAKER.length)];
  return f as string;
};

const VideomakerContenuto: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state, getClienteById, updateContenuto, toggleVideoRecorded, loadContenuti } = useData();
  const [clipConfirm, setClipConfirm] = useState(false);
  // Frase motivazionale: nuova a ogni caricamento della pagina, poi ruota ogni 3 minuti
  const [frase, setFrase] = useState<string>(() => fraseCasuale());
  useEffect(() => {
    const interval = setInterval(() => setFrase(prev => fraseCasuale(prev)), 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const contenuto = state.contenuti.find(c => c.id === id);
  const cliente = contenuto ? getClienteById(contenuto.clienteId) : undefined;

  if (!contenuto) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/videomaker')}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          <ArrowLeft size={18} /> Torna al Videomaker
        </button>
        <Card className="p-6 sm:p-8 text-center mt-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">Contenuto non trovato o non più disponibile.</p>
        </Card>
      </div>
    );
  }

  const applyClipCaricate = async (value: boolean) => {
    try {
      await updateContenuto(contenuto.id, { clipCaricate: value });
      await loadContenuti();
    } catch (error) {
      console.error('Errore clip caricate:', error);
      toast.error('Errore nell\'aggiornamento. Riprova.');
    }
  };

  const handleRegistrato = async () => {
    try {
      await toggleVideoRecorded(contenuto.id);
      await loadContenuti();
      navigate('/videomaker');
    } catch (error) {
      console.error('Errore registrazione:', error);
      toast.error('Errore nell\'aggiornamento. Riprova.');
    }
  };

  const sezioni = [
    { label: 'Gancio', value: contenuto.hook, accent: 'text-amber-600 dark:text-amber-400', ring: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' },
    { label: 'Corpo', value: contenuto.body, accent: 'text-blue-600 dark:text-blue-400', ring: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
    { label: 'CTA', value: contenuto.cta, accent: 'text-emerald-600 dark:text-emerald-400', ring: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          title="Indietro"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 dark:text-slate-100 min-w-0" style={{ letterSpacing: '-0.02em' }}>
              {contenuto.titolo}
            </h1>
            <StatusBadge stato={contenuto.stato} />
          </div>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
            <User className="w-3.5 h-3.5" /> {cliente?.nomeAzienda}
          </p>
        </div>
      </div>

      {/* Frase motivazionale/tecnica per il videomaker */}
      <div className="flex items-start gap-2 rounded-xl bg-[#fdf751]/10 dark:bg-[#fdf751]/[0.06] border border-[#fdf751]/40 dark:border-[#fdf751]/30 px-3 py-2.5">
        <Clapperboard className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#a89a00] dark:text-[#fdf751]" />
        <p className="text-sm font-semibold text-[#8f8300] dark:text-[#fdf751]">
          {frase}
        </p>
      </div>

      {/* Riferimenti / media (se presenti) */}
      {(contenuto.linkIspirazione || contenuto.copertinaUrl || contenuto.copyPost) && (
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          {contenuto.linkIspirazione && (
            <a href={contenuto.linkIspirazione} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:underline">
              Riferimento
            </a>
          )}
          {contenuto.copertinaUrl && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"><Image size={11} /> Copertina</span>
          )}
          {contenuto.copyPost && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">Copy</span>
          )}
        </div>
      )}

      {/* Script suddiviso in Gancio / Corpo / CTA */}
      <div className="space-y-3 sm:space-y-4">
        {sezioni.map((s, i) => (
          <Card key={s.label} className="p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-lg text-xs font-bold ${s.ring}`}>{i + 1}</span>
              <h2 className={`text-sm sm:text-base font-semibold uppercase tracking-wide ${s.accent}`}>{s.label}</h2>
            </div>
            <p className="text-sm sm:text-base text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
              {s.value || '—'}
            </p>
          </Card>
        ))}
      </div>

      {/* Azioni */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-1">
        <button
          onClick={() => (contenuto.clipCaricate ? applyClipCaricate(false) : setClipConfirm(true))}
          className={`flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 w-full sm:w-auto ${
            contenuto.clipCaricate
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
              : 'bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600'
          }`}
        >
          <CheckCircle className="h-4 w-4" /> Clip caricate
        </button>
        <Button
          variant="primary"
          disabled={!contenuto.clipCaricate}
          title={!contenuto.clipCaricate ? 'Prima spunta "Clip caricate"' : 'Segna come registrato'}
          onClick={handleRegistrato}
          className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0"
        >
          <Check className="h-4 w-4" /> Registrato
        </Button>
      </div>

      {/* Modal conferma clip */}
      <AnimatedModal isOpen={clipConfirm} onClose={() => setClipConfirm(false)} title="Clip caricate" size="sm">
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200/60 dark:border-emerald-800/40 flex items-center justify-center flex-shrink-0">
              <FolderCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed pt-1.5">
              Confermi che le clip sono nel posto corretto del Drive?
            </p>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
            <Button variant="secondary" onClick={() => setClipConfirm(false)} className="w-full sm:w-auto justify-center min-h-[44px]">Annulla</Button>
            <Button variant="primary" onClick={() => { setClipConfirm(false); applyClipCaricate(true); }} className="w-full sm:w-auto justify-center min-h-[44px]">
              <CheckCircle className="h-4 w-4" /> Conferma
            </Button>
          </div>
        </div>
      </AnimatedModal>
    </div>
  );
};

export default VideomakerContenuto;
